import {
  call,
  getContext,
  put,
  takeEvery,
  take,
  cancelled,
  fork,
  cancel,
  select,
} from "redux-saga/effects";
import { CONTEXT } from "@const/redux";
import {
  createConsentFormSuccess,
  createConsentFormFailure,
  getConsentFormsSuccess,
  getConsentFormsFailure,
  getConsentForms as getConsentFormsRequest,
  getReceivedForms as getReceivedFormsRequest,
  getNewSignConsentFormsCount as getNewSignConsentFormsCountRequest,
  deleteConsentFormSuccess,
  deleteConsentFormFailure,
  editConsentFormNameSuccess,
  editConsentFormNameFailure,
  getReceivedFormsSuccess,
  getReceivedFormsFailure,
  getPortionReceivedFormsSuccess,
  getPortionReceivedFormsFailure,
  getPortionConsentFormsSuccess,
  getPortionConsentFormsFailure,
  rejectReceivedFormSuccess,
  rejectReceivedFormFailure,
  signReceivedFormSuccess,
  signReceivedFormFailure,
  assignFormSuccess,
  assignFormFailure,
  getNewSignConsentFormsCountFailure,
  getNewSignConsentFormsCountSuccess,
  setSigningConsentFormId,
} from "./actions";
import {
  createConsentFormActionTypes,
  getConsentFormsActionTypes,
  getPortionConsentFormsActionTypes,
  editConsentFormNameActionTypes,
  deleteConsentFormActionTypes,
  getReceivedFormsActionTypes,
  getPortionReceivedFormsActionTypes,
  rejectReceivedFormActionTypes,
  signReceivedFormActionTypes,
  assignFormActionTypes,
  getNewSignConsentFormsCountActionTypes,
} from "./actionTypes";
import { completeMultipartUpload, uploadFilesSaga } from "../upload/saga";
import { normalizeConsentFormsArray } from "@helpers/consentForms";
import {
  initialOffset,
  paginationLimit,
  REJECT_STATUS,
  SIGN_STATUS,
} from "@const/consentForms";
import { removeEmptyKeys } from "@helpers/common";
import { eventChannel } from "redux-saga";
import {
  startSocketActionType,
  stopSocketActionType,
} from "../socket/actionTypes";

const createSocketChannel = ({ socket, signingConsentFormId }) => {
  return eventChannel((emit) => {
    socket.on("sign", () => {
      emit({
        type: signReceivedFormActionTypes.request,
        payload: { id: signingConsentFormId },
      });
    });
    socket.on("finish", () => {
      emit({
        type: setSigningConsentFormId.type,
        payload: { id: null },
      });
    });
    socket.on("cancel", () => {
      emit({
        type: setSigningConsentFormId.type,
        payload: { id: null },
      });
    });
    return () => {
      socket?.close();
    };
  });
};

function* watchSocketChannel() {
  const DropboxService = (yield call(() => import("@services/DropboxService")))
    .default;
  let channel = null;

  try {
    const socket = DropboxService.getClientInstance();
    channel = yield call(createSocketChannel, { socket });
    while (channel) {
      const action = yield take(channel);
      yield put(action);
    }
  } catch (e) {
    console.error(e);
  } finally {
    if (yield cancelled()) {
      channel?.close();
    }
  }
}

const createConsentFormAsync = ({ ConsentFormsService, body }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.createConsentForm(body);
};
const deleteConsentFormAsync = ({ ConsentFormsService, id }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.deleteConsentForm(id);
};

const editConsentFormNameAsync = ({ ConsentFormsService, id, formName }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.editConsentFormName(id, formName);
};

const getConsentFormsAsync = ({ ConsentFormsService, ...params }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.getConsentForms({ ...params });
};

const rejectReceivedFormAsync = ({ ConsentFormsService, ...params }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.updateStatus({
    body: { status: REJECT_STATUS },
    id: params.id,
  });
};

const signReceivedFormAsync = ({ ConsentFormsService, ...params }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.updateStatus({
    body: { status: SIGN_STATUS },
    id: params.id,
  });
};

const assignFormAsync = ({ ConsentFormsService, ...params }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.assignForm(params);
};
const getNewSignConsentFormsCountAsync = ({ ConsentFormsService }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.getNewSignConsentFormsCount();
};

const getReceivedFormsAsync = ({ ConsentFormsService, ...params }) => {
  if (!ConsentFormsService) {
    console.log("Required service is missing");
    return;
  }

  return ConsentFormsService.getConsentForms({ ...params });
};
function* createConsentForm({ payload }) {
  try {
    const {
      firstPageFileId,
      secondPageFileId,
      selfieFileId,
      signedConsentFormFileId,
    } = payload;

    const { ConsentFormsService, FilesService, ToasterService } =
      yield getContext(CONTEXT.SERVICES);

    const files = [
      firstPageFileId,
      secondPageFileId,
      selfieFileId,
      signedConsentFormFileId,
    ].filter((item) => item);

    const uploadedFiles = yield call(uploadFilesSaga, files);

    yield call(completeMultipartUpload, { FilesService, uploadedFiles });

    const body = {
      ...payload,
      firstPageFileId: uploadedFiles[0].fileId,
      secondPageFileId: secondPageFileId ? uploadedFiles[1].fileId : undefined,
      selfieFileId: uploadedFiles[secondPageFileId ? 2 : 1].fileId,
      signedConsentFormFileId: uploadedFiles[secondPageFileId ? 3 : 2].fileId,
    };

    yield call(createConsentFormAsync, {
      ConsentFormsService,
      body: removeEmptyKeys(body),
    });

    yield put(createConsentFormSuccess());
    yield put(
      getConsentFormsRequest({ limit: paginationLimit, offset: initialOffset }),
    );
    ToasterService.success("Consent form has been successfully created");
  } catch (error) {
    const { ToasterService } = yield getContext(CONTEXT.SERVICES);

    ToasterService.errorTranslated(
      error?.message || error.error?.message || "Something went wrong",
    );

    yield put(createConsentFormFailure(error));
  }
}

function* getConsentForms({ payload }) {
  try {
    const { ConsentFormsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getConsentFormsAsync, {
      ConsentFormsService,
      ...payload,
    });

    const { nextOffset, totalCount } = pagination;

    const {
      result: list,
      entities: { consentForms: formsById },
    } = normalizeConsentFormsArray(data);
    yield put(
      getConsentFormsSuccess({
        list,
        formsById,
        nextOffset,
        totalCount,
      }),
    );
  } catch (error) {
    yield put(getConsentFormsFailure(error));
  }
}

function* getReceivedForms({ payload }) {
  try {
    const { ConsentFormsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getReceivedFormsAsync, {
      ConsentFormsService,
      ...payload,
    });

    const { nextOffset, totalCount } = pagination;

    const {
      result: list,
      entities: { consentForms: formsById },
    } = normalizeConsentFormsArray(data);

    yield put(
      getReceivedFormsSuccess({
        list,
        formsById,
        nextOffset,
        totalCount,
      }),
    );
  } catch (error) {
    yield put(getReceivedFormsFailure(error));
  }
}

function* getPortionConsentForms({ payload }) {
  try {
    const { ConsentFormsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getConsentFormsAsync, {
      ConsentFormsService,
      ...payload,
    });

    const { nextOffset, totalCount } = pagination;

    const {
      result: list,
      entities: { consentForms: formsById },
    } = normalizeConsentFormsArray(data);

    yield put(
      getPortionConsentFormsSuccess({
        list,
        formsById,
        nextOffset,
        totalCount,
      }),
    );
  } catch (error) {
    yield put(getPortionConsentFormsFailure(error));
  }
}

function* getPortionReceivedForms({ payload }) {
  try {
    const { ConsentFormsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getReceivedFormsAsync, {
      ConsentFormsService,
      ...payload,
    });

    const { nextOffset, totalCount } = pagination;

    const {
      result: list,
      entities: { consentForms: formsById },
    } = normalizeConsentFormsArray(data);

    yield put(
      getPortionReceivedFormsSuccess({
        list,
        formsById,
        nextOffset,
        totalCount,
      }),
    );
  } catch (error) {
    yield put(getPortionReceivedFormsFailure(error));
  }
}
function* deleteConsentForm({ payload }) {
  try {
    const { ConsentFormsService, ToasterService } = yield getContext(
      CONTEXT.SERVICES,
    );

    yield call(deleteConsentFormAsync, {
      ConsentFormsService,
      id: payload.id,
    });
    yield put(deleteConsentFormSuccess());
    yield put(
      getConsentFormsRequest({ limit: paginationLimit, offset: initialOffset }),
    );
    yield put(getNewSignConsentFormsCountRequest());

    ToasterService.success("Consent form has been successfully deleted");
  } catch (error) {
    yield put(deleteConsentFormFailure(error));
  }
}
function* editConsentFormName({ payload }) {
  try {
    const { ConsentFormsService, ToasterService } = yield getContext(
      CONTEXT.SERVICES,
    );

    yield call(editConsentFormNameAsync, {
      ConsentFormsService,
      id: payload.id,
      formName: payload.formName,
    });

    yield put(editConsentFormNameSuccess());
    yield put(
      getConsentFormsRequest({ limit: paginationLimit, offset: initialOffset }),
    );
    ToasterService.success("Consent form has been successfully edited");
  } catch (error) {
    yield put(editConsentFormNameFailure(error));
  }
}

function* rejectReceivedForm({ payload }) {
  try {
    const { ConsentFormsService, ToasterService } = yield getContext(
      CONTEXT.SERVICES,
    );

    yield call(rejectReceivedFormAsync, {
      ConsentFormsService,
      ...payload,
    });

    yield put(rejectReceivedFormSuccess());
    yield put(
      getReceivedFormsRequest({
        limit: paginationLimit,
        offset: initialOffset,
        filterType: 2,
      }),
    );
    yield put(getNewSignConsentFormsCountRequest());
    ToasterService.success("Received form has been successfully rejected");
  } catch (error) {
    yield put(rejectReceivedFormFailure(error));
  }
}

function* signReceivedForm() {
  try {
    const { ConsentFormsService, ToasterService } = yield getContext(
      CONTEXT.SERVICES,
    );

    const { signingConsentFormId } = yield select(
      ({ consentFormsReducer }) => consentFormsReducer,
    );

    yield call(signReceivedFormAsync, {
      ConsentFormsService,
      id: signingConsentFormId,
    });

    yield put(signReceivedFormSuccess());
    yield put(
      getReceivedFormsRequest({
        limit: paginationLimit,
        offset: initialOffset,
        filterType: 2,
      }),
    );
    yield put(getNewSignConsentFormsCountRequest());

    ToasterService.success("Received form has been successfully signed");
  } catch (error) {
    yield put(signReceivedFormFailure(error));
  }
}
function* assignForm({ payload }) {
  try {
    const { ConsentFormsService } = yield getContext(CONTEXT.SERVICES);
    yield call(assignFormAsync, {
      ConsentFormsService,
      ...payload,
    });

    yield put(assignFormSuccess());
    yield put(
      getReceivedFormsRequest({
        limit: paginationLimit,
        offset: initialOffset,
        filterType: 2,
      }),
    );
    yield put(getNewSignConsentFormsCountRequest());
  } catch (error) {
    const { ToasterService } = yield getContext(CONTEXT.SERVICES);
    if (error.error.message) {
      ToasterService.error(error.error.message);
    }

    yield put(assignFormFailure(error));
  }
}

function* getNewSignConsentFormsCount() {
  try {
    const { ConsentFormsService } = yield getContext(CONTEXT.SERVICES);

    const { data } = yield call(getNewSignConsentFormsCountAsync, {
      ConsentFormsService,
    });
    yield put(
      getNewSignConsentFormsCountSuccess({
        newSignConsentFormsCount: data?.forSignCount || 0,
      }),
    );
  } catch (error) {
    yield put(getNewSignConsentFormsCountFailure(error));
  }
}

export function* watchCreateConsentForm() {
  yield takeEvery(createConsentFormActionTypes.request, createConsentForm);
}

export function* watchGetConsentForms() {
  yield takeEvery(getConsentFormsActionTypes.request, getConsentForms);
}

export function* watchGetPortionConsentForms() {
  yield takeEvery(
    getPortionConsentFormsActionTypes.request,
    getPortionConsentForms,
  );
}

export function* watchGetReceivedForms() {
  yield takeEvery(getReceivedFormsActionTypes.request, getReceivedForms);
}

export function* watchGetPortionReceivedForms() {
  yield takeEvery(
    getPortionReceivedFormsActionTypes.request,
    getPortionReceivedForms,
  );
}

export function* watchDeleteConsentForm() {
  yield takeEvery(deleteConsentFormActionTypes.request, deleteConsentForm);
}

export function* watchEditConsentFormName() {
  yield takeEvery(editConsentFormNameActionTypes.request, editConsentFormName);
}

export function* watchRejectReceivedForm() {
  yield takeEvery(rejectReceivedFormActionTypes.request, rejectReceivedForm);
}
export function* watchSignReceivedForm() {
  yield takeEvery(signReceivedFormActionTypes.request, signReceivedForm);
}

export function* watchAssignForm() {
  yield takeEvery(assignFormActionTypes.request, assignForm);
}

let socketTask;

function* startSocket() {
  socketTask = yield fork(watchSocketChannel);
}

function* stopSocket() {
  if (socketTask) {
    yield cancel(socketTask);
  }
}

export default function* watchConsentFormsSocket() {
  yield takeEvery(startSocketActionType, startSocket);
  yield takeEvery(stopSocketActionType, stopSocket);
}

export function* watchGetNewSignConsentFormsCount() {
  yield takeEvery(
    getNewSignConsentFormsCountActionTypes.request,
    getNewSignConsentFormsCount,
  );
}
